import { useRef, useState } from 'react';
import { Braces, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { ALERT_TEXT_NAMESPACE, placeholderTag } from './alertPlaceholders';
import {
    PICKER_ENTRIES,
    PICKER_GROUP_ICONS,
    PICKER_GROUPS,
    PLACEHOLDER_ICONS,
    PickerEntry,
    pickerEntriesFor,
    pickerExample,
} from './alertPlaceholderPicker';
import { PLACEHOLDER_DRAG_TYPE } from './PlaceholderTextField';
import { loadRecents, saveRecents, withRecent } from './placeholderRecents';

type Translate = (key: string, values?: Record<string, string>) => string;

const PICKER = `${ALERT_TEXT_NAMESPACE}picker.`;

/**
 * Plain substring search over the token name, label and example value.
 *
 * cmdk's default is a fuzzy subsequence match, which finds "Aufgedruckte" for "druck"; here a
 * query must actually occur in the entry.
 */
const matches = (value: string, search: string, keywords?: string[]): number =>
    [value, ...(keywords ?? [])].some((text) => text.toLowerCase().includes(search.trim().toLowerCase())) ? 1 : 0;

/**
 * "Insert placeholder": a searchable, grouped list of what a message can name.
 *
 * Each entry says what it is (label), what it means (one line) and what it looks like (sample
 * value). Enter or a click hands the token name to `onInsert` and closes; Shift+Enter / Shift+click
 * inserts and stays open. Recently used names come first, names already in the field are ticked, and
 * an entry can also be dragged into a field. The uplink group exists only for "days without uplink".
 */
export default function PlaceholderPicker(properties: {
    t: Translate;
    /** Name of the field the picker inserts into — for the accessible name only. */
    fieldLabel: string;
    noUplink: boolean;
    language?: string;
    /** Names already used in the field: marked, still insertable. */
    usedNames?: string[];
    /** Opened from outside (keyboard shortcut in the field). */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    onInsert: (name: string, keepOpen: boolean) => void;
}) {
    const { t } = properties;
    const [ownOpen, setOwnOpen] = useState(false);
    const open = properties.open ?? ownOpen;
    // While an entry is dragged out the list gets out of the way, so the fields underneath can be hit.
    const [dragging, setDragging] = useState(false);
    const [recents, setRecents] = useState<string[]>(loadRecents);
    const [highlighted, setHighlighted] = useState('');
    const [search, setSearch] = useState('');
    const stayOpen = useRef(false);
    const entries = pickerEntriesFor({ noUplink: properties.noUplink });
    // For a "days without uplink" rule the uplink values are what the message is about: first.
    const orderedGroups = properties.noUplink
        ? ['uplink' as const, ...PICKER_GROUPS.filter((group) => group !== 'uplink')]
        : PICKER_GROUPS;
    const recentEntries = recents
        .map((name) => entries.find((entry) => entry.name === name))
        .filter((entry): entry is PickerEntry => !!entry);
    const used = new Set(properties.usedNames ?? []);
    const highlightedEntry = PICKER_ENTRIES.find((entry) => entry.name === highlighted.replace(/^recent-/, ''));

    const setOpen = (next: boolean) => {
        setOwnOpen(next);
        properties.onOpenChange?.(next);

        if (!next) {
            setSearch('');
        }
    };

    const insert = (name: string) => {
        const next = withRecent(recents, name);

        setRecents(next);
        saveRecents(next);
        properties.onInsert(name, stayOpen.current);

        if (!stayOpen.current) {
            setOpen(false);
        }
    };

    const renderItem = (entry: PickerEntry, key: string) => {
        const label = t(`${PICKER}items.${entry.key}.label`);
        const hint = t(`${PICKER}items.${entry.key}.hint`);
        const example = pickerExample(entry.name, properties.language);
        const Icon = PLACEHOLDER_ICONS[entry.key];

        return (
            <CommandItem
                key={key}
                value={key}
                keywords={[entry.name, label, example]}
                title={placeholderTag(entry.name)}
                className="cursor-grab flex-col items-stretch gap-0.5 active:cursor-grabbing"
                draggable
                onDragStart={(event: React.DragEvent) => {
                    event.dataTransfer.effectAllowed = 'copy';
                    event.dataTransfer.setData(PLACEHOLDER_DRAG_TYPE, placeholderTag(entry.name));
                    event.dataTransfer.setData('text/plain', placeholderTag(entry.name));
                    window.setTimeout(() => setDragging(true), 0);
                }}
                onDragEnd={() => {
                    setDragging(false);
                    setOpen(false);
                }}
                onSelect={() => insert(entry.name)}
            >
                <span className="flex min-w-0 items-baseline justify-between gap-3">
                    <span className="flex items-center gap-2 font-medium">
                        <Icon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {label}
                        {used.has(entry.name) && (
                            <Check aria-label={t(`${PICKER}inUse`)} className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                    </span>
                    <span className="min-w-0 truncate font-mono text-xs text-muted-foreground">{example}</span>
                </span>
                <span className="pl-6 text-xs text-muted-foreground">{hint}</span>
            </CommandItem>
        );
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs text-muted-foreground"
                    aria-label={t(`${PICKER}insertInto`, { field: properties.fieldLabel })}
                    title={t(`${PICKER}shortcut`)}
                >
                    <Braces aria-hidden className="h-3.5 w-3.5" />
                    {t(`${PICKER}trigger`)}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className={`w-[min(380px,calc(100vw-2rem))] p-0 duration-150 motion-reduce:animate-none ${dragging ? 'pointer-events-none opacity-0' : ''}`}
                // The caller moves the caret back into the field; the trigger must not take focus.
                onCloseAutoFocus={(event) => event.preventDefault()}
            >
                <Command
                    filter={matches}
                    value={highlighted}
                    onValueChange={setHighlighted}
                    onKeyDownCapture={(event) => { stayOpen.current = event.key === 'Enter' && event.shiftKey; }}
                    onPointerDownCapture={(event) => { stayOpen.current = event.shiftKey; }}
                >
                    <CommandInput placeholder={t(`${PICKER}search`)} value={search} onValueChange={setSearch} />
                    <CommandList className="max-h-[320px]">
                        <CommandEmpty>{t(`${PICKER}empty`)}</CommandEmpty>
                        {search.trim() === '' && recentEntries.length > 0 && (
                            <CommandGroup heading={t(`${PICKER}recent`)}>
                                {recentEntries.map((entry) => renderItem(entry, `recent-${entry.name}`))}
                            </CommandGroup>
                        )}
                        {orderedGroups.map((group) => {
                            const inGroup = entries.filter((entry) => entry.group === group);
                            const GroupIcon = PICKER_GROUP_ICONS[group];

                            if (inGroup.length === 0) {
                                return null;
                            }

                            return (
                                <CommandGroup
                                    key={group}
                                    heading={(
                                        <span className="flex items-center gap-1.5">
                                            <GroupIcon aria-hidden className="h-3.5 w-3.5" />
                                            {t(`${PICKER}groups.${group}`)}
                                        </span>
                                    )}
                                >
                                    {inGroup.map((entry) => renderItem(entry, entry.name))}
                                </CommandGroup>
                            );
                        })}
                    </CommandList>
                    <div
                        aria-live="polite"
                        className="flex min-h-9 items-center gap-2 border-t px-3 py-1.5 text-xs text-muted-foreground"
                    >
                        {highlightedEntry
                            ? (
                                <>
                                    <span>{t(`${PICKER}previewLabel`)}</span>
                                    <span className="min-w-0 truncate rounded bg-primary/10 px-1.5 py-0.5 text-foreground">
                                        {pickerExample(highlightedEntry.name, properties.language)}
                                    </span>
                                </>
                            )
                            : null}
                        <span className="ml-auto shrink-0">{t(`${PICKER}stayOpenHint`)}</span>
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
